import React from 'react'
import { useTranslation } from 'react-i18next'

import { Body1, Heading3 } from '@ui/Typography'
import { Button } from '@ui/components/Button'
import { RichIcon } from '@ui/components/RichIcon'
import { ArrowLeftRightIcon } from '@ui/icons/ArrowLeftRight.icon'
import { BankIcon } from '@ui/icons/Bank.icon'
import { DepositIcon } from '@ui/icons/Deposit.icon'
import { GraphIcon } from '@ui/icons/Graph.icon'
import { SuitcaseIcon } from '@ui/icons/Suitcase.icon'
import { WalletIcon } from '@ui/icons/Wallet.icon'

import { isOne } from '@utils/validations'

import { Box } from '@shared/components/Box'

interface Props {
  stepNumber: number
  nextScreen: () => void
  startJourney: () => void
}

export const JourneyStep: React.FC<Props> = ({ stepNumber, nextScreen, startJourney }) => {
  const { t } = useTranslation()

  if (isOne(stepNumber)) {
    return (
      <Box
        color='text-low-em'
        textAlign='center'
        flex
        align='center'
        direction='column'
        pt='x48'
        gap='x48'
      >
        <Heading3 semibold>{t('What is Tickmill?')}</Heading3>
        <Body1 color='text-low-em'>
          {t('Tickmill is NOT a trading platform. At least not yet')}
          <br />
          {t('Tickmill is your')} <Body1 semibold> {t('personal wallet')}</Body1>{' '}
          {t('between your bank account and trading platform.')}
        </Body1>

        <Box flex align='center' gap='x48'>
          <Box flex direction='column' align='center' gap='x8'>
            <BankIcon size={65} color='text-base-em' />
            <Box flex direction='column'>
              <Body1>
                {t('Your')}
                <br />
                {t('Bank')}
              </Body1>
            </Box>
          </Box>
          <ArrowLeftRightIcon size={50} color='text-base-em' />
          <Box flex direction='column' align='center' gap='x8'>
            <WalletIcon size={65} color='text-base-em' />
            <Body1>
              Tickmill
              <br />
              {t('Trading Account.Wallet')}
            </Body1>
          </Box>
          <ArrowLeftRightIcon size={50} color='text-base-em' />
          <Box flex direction='column' align='center' gap='x8'>
            <GraphIcon size={65} color='text-base-em' />
            <Body1>
              {t('Trading')}
              <br />
              {t('Platform')}
            </Body1>
          </Box>
        </Box>

        <Button.Primary onClick={nextScreen}>{t('Next')}</Button.Primary>
      </Box>
    )
  }

  return (
    <Box
      color='text-low-em'
      textAlign='center'
      flex
      align='center'
      direction='column'
      pt='x48'
      gap='x48'
    >
      <Heading3 semibold>{t('First steps')}</Heading3>
      <Box flex align='center' gap='x48'>
        <Box flex direction='column' align='center' gap='x8'>
          <WalletIcon size={75} color='text-base-em' />
          <Body1 isParagraph medium>
            {t('Create a Wallet')}
          </Body1>
          <RichIcon
            size='x32'
            shape='circle'
            bg='surface-surface-2'
            labelColor='text-high-em'
            label='1'
          />
        </Box>

        <Box flex direction='column' align='center' gap='x8'>
          <SuitcaseIcon size={75} color='text-base-em' />
          <Body1 isParagraph medium>
            {t('Create a trading account')}
          </Body1>
          <RichIcon
            size='x32'
            shape='circle'
            bg='surface-surface-2'
            labelColor='text-high-em'
            label='2'
          />
        </Box>

        <Box flex direction='column' align='center' gap='x8'>
          <DepositIcon size={75} color='text-base-em' />
          <Body1 isParagraph medium>
            {t('Deposit funds')}
          </Body1>
          <RichIcon
            size='x32'
            shape='circle'
            bg='surface-surface-2'
            labelColor='text-high-em'
            label='3'
          />
        </Box>
      </Box>
      <Body1 color='text-low-em'>{t("...and your'r ready to go!")}</Body1>
      <Button.Primary onClick={startJourney}>{t('Start your journey')}</Button.Primary>
    </Box>
  )
}
